import { useCallback, useContext, useMemo, useRef, type ReactNode } from 'react'
import { useBeforeUnload, useBlocker, useParams, type BlockerFunction } from 'react-router-dom'
import { Modal as BootstrapModal } from 'react-bootstrap'

import { Modal } from '../../components/Modal/Modal'

import { useAccountHasDocumentZdrPolicy } from './hooks'
import { DocumentLeaveBypassContext, type DocumentLeaveBypassContextValue } from './DocumentLeaveBypassContext'

export const DocumentLeaveGuardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const bypassRef = useRef(false)

  const value = useMemo<DocumentLeaveBypassContextValue>(
    () => ({
      bypassOnce: () => {
        bypassRef.current = true
      },
      consumeBypass: () => {
        if (bypassRef.current) {
          bypassRef.current = false
          return true
        }
        return false
      },
    }),
    [bypassRef],
  )

  return (
    <DocumentLeaveBypassContext.Provider value={value}>
      <DocumentLeaveGuard />
      {children}
    </DocumentLeaveBypassContext.Provider>
  )
}

const useDocumentLeaveGuard = () => {
  const accountHasZDRPolicy = useAccountHasDocumentZdrPolicy()
  const { documentBatchId = null, documentId = null } = useParams()
  const context = useContext(DocumentLeaveBypassContext)

  const root = documentBatchId ? `/document-batches/${documentBatchId}` : documentId ? `/documents/${documentId}` : null

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) => {
        if (!accountHasZDRPolicy || !root || currentLocation.pathname === nextLocation.pathname) {
          return false
        }

        if (context?.consumeBypass()) {
          return false
        }

        const { pathname } = nextLocation
        const isInFlow = pathname === root || pathname.startsWith(`${root}/`)

        return !isInFlow
      },
      [accountHasZDRPolicy, root, context],
    ),
  )

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (accountHasZDRPolicy) {
          e.preventDefault()
          e.returnValue = ''
        }
      },
      [accountHasZDRPolicy],
    ),
  )

  const confirmLeave = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  const cancelLeave = () => {
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }

  return { showModal: blocker.state === 'blocked', confirmLeave, cancelLeave, isBatch: !!documentBatchId }
}

const DocumentLeaveGuard: React.FC = () => {
  const { showModal, confirmLeave, cancelLeave, isBatch } = useDocumentLeaveGuard()

  return <DocumentLeaveConfirmationModal show={showModal} onConfirm={confirmLeave} onCancel={cancelLeave} isBatch={isBatch} />
}

interface DocumentLeaveConfirmationModalProps {
  show: boolean
  onConfirm: () => void
  onCancel: () => void
  isBatch: boolean
}

const DocumentLeaveConfirmationModal: React.FC<DocumentLeaveConfirmationModalProps> = ({ show, onConfirm, onCancel, isBatch }) => {
  return (
    <Modal
      show={show}
      onHide={onCancel}
      header={<BootstrapModal.Title>Zero data retention is set</BootstrapModal.Title>}
      headerBodySeparator={true}
      bodyClassName='zocks-pb-0.75'
      body={
        <span>
          Your account has zero data retention policy for documents, so Zocks doesn't store any of them. By leaving the {isBatch ? 'package' : 'document'} all
          unsynced insights will be lost.
        </span>
      }
      footerClassName='zocks-pt-0.75 zocks-px-1.5 zocks-pb-1.5'
      showFooterActions={true}
      onFooterPrimaryAction={onConfirm}
      footerPrimaryActionTitle={isBatch ? 'Leave package' : 'Leave document'}
      footerPrimaryActionButtonVariant='danger'
    />
  )
}
